import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import './PublicFeedbackBubble.css'

/*
 * Burbuja de feedback pública (sin sesión) para las landings.
 *
 * Envía a POST {apiBase}/projects/feedback-public/, que guarda el reporte en
 * projects.FeedbackReport (estado nuevo, sin reporter) y avisa por correo.
 * Anti-bot del lado del backend: throttle por IP, honeypot `website` y tiempo
 * mínimo de diligenciamiento (`elapsed_ms`). El backend responde 201 también
 * cuando descarta un bot, así que el mensaje de éxito es siempre el mismo.
 *
 * Copia autocontenida (mismo archivo en ecf-coop-landing, ecf-inmo-landing y
 * landy-react-template-master): @ecf/feedback requiere sesión y multipart.
 */

export type PublicFeedbackApp = 'landing_corp' | 'landing_coop' | 'landing_inmo'

interface PublicFeedbackBubbleProps {
  apiBase: string
  app: PublicFeedbackApp
  /** Lado de la pantalla donde se ancla la burbuja (evita chocar con otros widgets). */
  side?: 'left' | 'right'
}

const CATEGORIES = [
  { value: 'mejora', label: 'Sugerencia' },
  { value: 'bug', label: 'Error' },
  { value: 'pregunta', label: 'Pregunta' },
]
const MAX_LENGTH = 2000
// Ids fijos: la burbuja se monta una sola vez por página (el hook de ids de React 18 no está en los tipos de landy).
const panelId = 'pfb-panel'
const titleId = 'pfb-title'
const messageId = 'pfb-message'

type Status = { type: '' | 'ok' | 'error'; text: string }

function buildCapture() {
  // Solo contexto técnico no sensible: sin query string, cookies ni almacenamiento.
  return {
    path: window.location.pathname,
    referrer: document.referrer ? document.referrer.split('?')[0] : '',
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    user_agent: navigator.userAgent,
    timestamp: new Date().toISOString(),
  }
}

function IconChat() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  )
}

function IconClose() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

export default function PublicFeedbackBubble({ apiBase, app, side = 'left' }: PublicFeedbackBubbleProps) {
  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState('mejora')
  const [message, setMessage] = useState('')
  const [website, setWebsite] = useState('')
  const [status, setStatus] = useState<Status>({ type: '', text: '' })
  const [sending, setSending] = useState(false)
  const openedAt = useRef(0)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!open) return undefined
    textareaRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const toggle = () => {
    if (!open) {
      openedAt.current = Date.now()
      setStatus({ type: '', text: '' })
    }
    setOpen((current) => !current)
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!message.trim()) {
      setStatus({ type: 'error', text: 'Escribe tu comentario antes de enviarlo.' })
      return
    }
    setSending(true)
    setStatus({ type: '', text: '' })
    try {
      const response = await fetch(`${apiBase.replace(/\/+$/, '')}/projects/feedback-public/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          app,
          category,
          message: message.trim(),
          url: `${window.location.origin}${window.location.pathname}`,
          website,
          elapsed_ms: Date.now() - openedAt.current,
          capture: buildCapture(),
        }),
      })
      if (response.status === 429) {
        setStatus({ type: 'error', text: 'Recibimos varios comentarios desde tu conexión. Inténtalo de nuevo más tarde.' })
      } else if (!response.ok) {
        setStatus({ type: 'error', text: 'No pudimos enviar tu comentario. Revisa el texto e inténtalo de nuevo.' })
      } else {
        setMessage('')
        setStatus({ type: 'ok', text: 'Gracias, recibimos tu comentario.' })
      }
    } catch {
      setStatus({ type: 'error', text: 'No hay conexión con el servidor. Inténtalo de nuevo en unos minutos.' })
    } finally {
      setSending(false)
    }
  }

  return (
    <div className={`pfb pfb--${side}`}>
      {open && (
        <div className="pfb__panel" id={panelId} role="dialog" aria-modal="false" aria-labelledby={titleId}>
          <div className="pfb__head">
            <div>
              <h2 className="pfb__title" id={titleId}>Cuéntanos qué piensas</h2>
              <p className="pfb__subtitle">Tu comentario es anónimo y nos ayuda a mejorar este sitio.</p>
            </div>
            <button
              type="button"
              className="pfb__close"
              onClick={() => {
                setOpen(false)
                triggerRef.current?.focus()
              }}
              aria-label="Cerrar comentarios"
            >
              <IconClose />
            </button>
          </div>

          <form className="pfb__body" onSubmit={onSubmit} noValidate>
            <fieldset className="pfb__fieldset">
              <legend className="pfb__label">Tipo de comentario</legend>
              <div className="pfb__options">
                {CATEGORIES.map((option) => (
                  <label key={option.value} className={`pfb__option${category === option.value ? ' is-active' : ''}`}>
                    <input
                      type="radio"
                      name="pfb-category"
                      value={option.value}
                      checked={category === option.value}
                      onChange={() => setCategory(option.value)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="pfb__label" htmlFor={messageId}>Comentario</label>
            <textarea
              ref={textareaRef}
              id={messageId}
              className="pfb__textarea"
              rows={4}
              maxLength={MAX_LENGTH}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="¿Qué encontraste o qué te gustaría ver?"
              required
            />
            <span className="pfb__counter" aria-hidden="true">{message.length}/{MAX_LENGTH}</span>

            {/* Honeypot: invisible para personas y lectores de pantalla. */}
            <div className="pfb__hp" aria-hidden="true">
              <label>
                Sitio web
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                />
              </label>
            </div>

            <p className={`pfb__status${status.type ? ` is-${status.type}` : ''}`} role="status" aria-live="polite">
              {status.text}
            </p>

            <button type="submit" className="pfb__submit" disabled={sending}>
              {sending ? 'Enviando...' : 'Enviar comentario'}
            </button>
          </form>
        </div>
      )}

      <button
        ref={triggerRef}
        type="button"
        className={`pfb__trigger${open ? ' is-open' : ''}`}
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={open ? 'Cerrar comentarios' : 'Enviar un comentario sobre este sitio'}
      >
        {open ? <IconClose /> : <IconChat />}
      </button>
    </div>
  )
}
